import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { t } from 'i18next';
import moment from 'moment';
import React, { useState, useRef } from 'react';
import { FaEdit } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import { useToast } from '../../core/contexts/toast';
import { formatarPorcentagem, formatLargeNumber, isNumeric } from '../../core/utils/funcoes';
import { ModeloSopInventory } from '../../core/models/sopInventory';
import { RepositorioSopInventory } from '../../core/repositories/sopInventoryRepositorio';

interface Props {
  lista: ModeloSopInventory[];
  setLista: React.Dispatch<React.SetStateAction<ModeloSopInventory[]>>;
}

const SopInventoryTable: React.FC<Props> = (props) => {
  const gridApi = useRef<any>(null);
  const { mostrarToast } = useToast();
  const [loading, setLoading] = useState(false);

  const columnDefs: ColDef<ModeloSopInventory>[] = [
    { headerName: 'Business Unit', field: 'des_business_unit', minWidth: 120 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Month/Year', field: 'des_time', minWidth: 100 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={true} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Manufacturing Plant', field: 'des_industrial_plant', minWidth: 120 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Product', field: 'des_product', minWidth: 120 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },

    { headerName: 'Inventory Real (units)', field: 'num_fg_inventory_real_units', minWidth: 120 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Inventory Forecast (units)', field: 'num_fg_inventory_forecast_units', minWidth: 140 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Inventory Budget (units)', field: 'num_fg_inventory_budget_units', minWidth: 140 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },

    { headerName: 'Inventory Real (USD)', field: 'num_fg_inventory_real_usd', minWidth: 130 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Inventory Forecast (USD)', field: 'num_fg_inventory_forecast_usd', minWidth: 150 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Inventory Budget (USD)', field: 'num_fg_inventory_budget_usd', minWidth: 150 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },

    { headerName: 'Unit Cost Real (USD/unit)', field: 'num_fg_inventory_unitcost_real', minWidth: 150 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Unit Cost Forecast (USD/unit)', field: 'num_fg_inventory_unitcost_forecast', minWidth: 170 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Unit Cost Budget (USD/unit)', field: 'num_fg_inventory_unitcost_budget', minWidth: 170 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },

    { headerName: 'RM Inventory Real (USD)', field: 'num_rm_inventory_real_usd', minWidth: 160 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'WIP Inventory Real (USD)', field: 'num_wip_inventory_real_usd', minWidth: 160 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Others/MRO Inventory Real (USD)', field: 'num_othersmro_inventory_real_usd', minWidth: 200 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },

    { headerName: 'Total Inventory Real (USD)', field: 'num_total_inventory_real_usd', minWidth: 180 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Total Unit Cost Real (USD/unit)', field: 'num_total_unitcost_real_usd', minWidth: 190 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },

    { headerName: 'Turn', field: 'turn', minWidth: 80 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'DOH', field: 'doh', minWidth: 80 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
  ];

  const onGridReady = (params: any) => {
    gridApi.current = params.api;
    gridApi.current.sizeColumnsToFit();
  };

  const CustomSkeleton = (props: { value: any; money?: boolean; date?: boolean; porcetagem?: boolean, attr?: string, linha?: ModeloSopInventory }) => {
    return (
      <>
        {loading ? (
          <Skeleton className="width-100" />
        ) : (
          <label className='text-capitalize'>
            {(localStorage.getItem("editando") && (typeof props.value === "number" || props.date)) && (
              <label onClick={() => { atualizarValor(props.attr || "", props.linha || new ModeloSopInventory()) }} className="cursor-pointer">&nbsp;<FaEdit /></label>
            )}
            {props.value ? props.date ? (
              moment(props.value).format("MMM-YYYY")
            ) : typeof props.value === "number" ? (
              props.porcetagem ?
                formatarPorcentagem(props.value / 100, 2)
                : formatLargeNumber(props.value, props.money ? t("components.R$") : "")
            ) : (
              props.value
            ) : "-"}
          </label>
        )}
      </>
    );
  };

  async function atualizarValor(atributo: string, linha: ModeloSopInventory) {
    const valor = prompt(`Por favor, insira um valor ${atributo} (${(linha as any)[atributo]}):`);
    if (valor) {
      const _centro = new RepositorioSopInventory();
      _centro.atualizarValor(atributo, valor, linha).then((atualizadas) => {
        if (atualizadas) {
          var encontra = props.lista.find(x => x.cod_tela_sop_inventory === linha.cod_tela_sop_inventory);
          if (encontra) (encontra as any)[atributo] = isNumeric(valor) ? Number.parseFloat(valor) : valor;
          props.setLista([...props.lista]);
        }
        mostrarToast({
          titulo: "Sucesso",
          mensagem: `${atualizadas} linha(s) atualizada(s)`,
          tipo: "success",
        });
      }).catch(err => {
        mostrarToast({
          titulo: "Erro",
          mensagem: t("pages.Aconteceu algo inesperado, por favor tente novamente mais tarde!"),
          tipo: "error",
        });
      });
    }
  }

  return (
    <div className="ag-theme-alpine" style={{ height: 600, width: '100%' }}>
      <AgGridReact<ModeloSopInventory>
        rowData={props.lista}
        columnDefs={columnDefs}
        defaultColDef={{
          sortable: true,
          filter: true,
          resizable: true,
          headerClass: 'wrap-header',
          cellClass: 'numeric-cell'
        }}
        pagination={true}
        paginationPageSize={20}
        domLayout="autoHeight"
        onGridReady={onGridReady}
      />
    </div>
  );
};

export default SopInventoryTable;
